import {
  AutocompleteGenericEntity,
  FetchAutocomplete,
  FetchAutocompleteChangeReason,
  FetchAutocompleteProps,
} from '@australiangreens/ag-internal-components';

import { isTemplatePlaceholder, TemplatePlaceholder } from '@/types';
import { DEFAULT_PLACEHOLDER_FIELD_LABEL } from '@/constants/searchTemplate';
import { useState } from 'react';
import { createTemplatePlaceholder } from '@/util/createTemplatePlaceholder';
import TemplatePlaceholderContextMenu, {
  CloseOutcome,
  ContextMenuState,
  handleOpenPlaceholderContextMenu,
} from './TemplatePlaceholderContextMenu';
import { PLACEHOLDER_BLUE } from './colours';

export type TemplateCapableFetchAutocompleteChangeReason =
  | FetchAutocompleteChangeReason
  | 'setIsTemplatePlaceholder'
  | 'unsetIsTemplatePlaceholder';

export type TemplateCapableFetchAutocompleteProps<T extends AutocompleteGenericEntity> = Omit<
  FetchAutocompleteProps<T>,
  'value' | 'onChange'
> & {
  value: FetchAutocompleteProps<T>['value'] | TemplatePlaceholder;
  onChange: (
    newValue: FetchAutocompleteProps<T>['value'] | TemplatePlaceholder,
    reason: TemplateCapableFetchAutocompleteChangeReason
  ) => void;
  isOnTemplatePage?: boolean;

  templateBuilderDisplayName?: string;
};

export const PLACEHOLDER_GREEN = `rgba(71, 149, 62, 0.08)`;

export default function TemplateCapableFetchAutocomplete<T extends AutocompleteGenericEntity>({
  value,
  isOnTemplatePage = false,
  onChange,
  templateBuilderDisplayName = '',
  ...props
}: TemplateCapableFetchAutocompleteProps<T>) {
  const [placeholderMenuState, setPlaceholderMenuState] = useState<ContextMenuState>(null);

  const valueIsPlaceholder = isTemplatePlaceholder(value);

  const placeholderProps = valueIsPlaceholder
    ? {
        // The following gives it the placeholder styling
        textFieldColor: 'info' as FetchAutocompleteProps<T>['textFieldColor'],
        placeholderText: DEFAULT_PLACEHOLDER_FIELD_LABEL,
        textFieldFocused: true,
        textFieldSx: {
          '& .MuiFilledInput-root.Mui-focused': {
            backgroundColor: PLACEHOLDER_BLUE,
          },
          '& .MuiOutlinedInput-root.Mui-focused': {
            backgroundColor: PLACEHOLDER_BLUE,
          },
          caretColor: 'transparent',
        },
        readOnly: true,
        hideInputEndAdornment: true,
      }
    : undefined;

  const handleCloseTemplatePlaceholderContextMenu = (reason: CloseOutcome) => {
    setPlaceholderMenuState(null);
    if (reason === 'setIsTemplatePlaceholder') {
      onChange(
        // TODO: Allow setting the placeholder title etc
        createTemplatePlaceholder(templateBuilderDisplayName),
        'setIsTemplatePlaceholder'
      );
    } else if (reason === 'unsetIsTemplatePlaceholder') {
      onChange([], 'unsetIsTemplatePlaceholder');
    }
  };

  if (isOnTemplatePage) {
    return (
      <>
        <FetchAutocomplete
          disableDefaultRightClickBehaviour
          onRightClick={(event) => {
            handleOpenPlaceholderContextMenu(event, placeholderMenuState, setPlaceholderMenuState);
          }}
          value={valueIsPlaceholder ? [] : value}
          onChange={onChange}
          {...placeholderProps}
          {...props}
        />
        <TemplatePlaceholderContextMenu
          menuState={placeholderMenuState}
          placeholderActive={valueIsPlaceholder}
          onClose={handleCloseTemplatePlaceholderContextMenu}
        />
      </>
    );
  }

  if (valueIsPlaceholder) {
    throw new Error(
      'TemplateCapableFetchAutocomplete: value is a placeholder but isOnTemplatePage is false'
    );
  }

  return <FetchAutocomplete value={value} onChange={onChange} {...props} />;
}
